import { useState, useEffect, } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Construction } from 'lucide-react';

const services = [
  {
    slug: 'pool-maintenance',
    title: 'Pool Maintenance',
    description: `Keeping your pool in perfect condition year-round requires expert care. Our routine maintenance services include:

Water testing & chemical balancing to keep the pool safe and clean
Filter & pump inspections to ensure optimal performance
Debris removal & vacuuming for crystal-clear water
Algae treatment & prevention`,
    image: '/Pool Maintenance.png'
  },
  {
    slug: 'repairs-equipment',
    title: 'Repairs & Equipment',
    description: `Is your pool equipment not working properly? We provide fast and efficient repairs for:

Pumps, filters, and skimmers
Pool leaks and plumbing issues
Lights and electrical components

We diagnose and fix problems quickly, so you can enjoy your pool without worries.`,
    image: '/Repairs & Equipment.png'
  },
  {
    slug: 'heat-pumps',
    title: 'Heat Pumps',
    description: 'Enjoy swimming all year round with energy-efficient pool heating solutions...',
    image: '/Heat Pumps.png'
  
  },
  {
    slug: 'new-pool-installation',
    title: 'New Pool Installation',
    description: `We specialize in complete pool installation, ensuring a fully functional and high-quality setup. Whether it's a residential or commercial pool, our team handles:

Piping & filtration systems
Pump, skimmer, and drainage installation
Lighting, heating, and automation setup

We do not provide pool design but work closely with builders and architects to install everything your pool needs to function perfectly.

Get in touch today for a consultation!`,
    icon: Construction,
    image: '/New Pool Installation.png'

  },
  {
    slug: 'water-care',
    title: 'Water Care',
    description: `Balanced water = a healthy pool. We offer:

Professional water testing & analysis
Chemical treatment for safety & hygiene
Algae removal & prevention
Saltwater system maintenance

Say goodbye to murky, unsafe water—we keep it crystal clear!`,
    image: '/Water Care.png'
  }
];

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  // Get current service index from route param
  const currentIndex = services.findIndex(s => s.slug === slug);

  // Auto-rotate between services every few seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!isTransitioning) {
        goToService('next');
      }
    }, 6000);
    return () => clearInterval(intervalId);
  }, [isTransitioning, currentIndex, slug]);
  
  const goToService = (direction: 'next' | 'prev') => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % services.length
      : (currentIndex - 1 + services.length) % services.length;
      
    navigate(`/services/${services[newIndex].slug}`);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  if (!service) {
    return (
      <div className="min-h-screen py-20">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-2xl text-gray-900">Service not found</h1>
          <Link to="/#services" className="text-cyan-600 hover:text-cyan-700">
            Return to services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16">
      <div className={`max-w-5xl mx-auto px-4 transition-opacity duration-500 ${
        isTransitioning ? 'opacity-0' : 'opacity-100'
      }`}>
        {/* Service Image with Navigation Arrows */}
        <div className="relative mb-12 group">
          {/* Left Arrow */}
          <button
            onClick={() => goToService('prev')}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-10 opacity-0 group-hover:opacity-100"
            disabled={isTransitioning}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Image */}
          <div className="rounded-2xl overflow-hidden shadow-2xl transform transition-transform duration-500 hover:scale-[1.02]">
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* Right Arrow */}
          <button
            onClick={() => goToService('next')}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-10 opacity-0 group-hover:opacity-100"
            disabled={isTransitioning}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Service Content */}
        <div className="max-w-3xl mx-auto">
          {/* Service Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{service.title}</h1>
            <div className="w-24 h-1 bg-cyan-500 mx-auto rounded-full"></div>
          </div>
          
          {/* Service Description */}
          <div className="prose prose-lg max-w-none">
            <div className="backdrop-blur-sm bg-white/50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{service.description}</p>
            </div>
          </div>
        </div>

        {/* Service Navigation Indicators */}
        <div className="mt-12 flex justify-center space-x-3">
          {services.map((s, index) => (
            <button
              key={s.slug}
              onClick={() => {
                if (!isTransitioning && index !== currentIndex) {
                  setIsTransitioning(true);
                  navigate(`/services/${s.slug}`);
                  setTimeout(() => setIsTransitioning(false), 500);
                }
              }}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-cyan-500 scale-125'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              disabled={isTransitioning}
              aria-label={`Go to ${s.title}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail; 